import {
  CheckCircleFilled,
  CloseOutlined,
  FileTextOutlined,
  InstagramOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import React, { useMemo } from "react";
import { isMobile, isTablet } from "react-device-detect";
import { useNavigate } from "react-router-dom";

export interface CampaignPostCreateRecommendation {
  id: "social" | "description" | "logo";
  title: string;
  description: string;
  ctaLabel: string;
  onClick: () => void;
}

interface CampaignPostCreateSuccessModalProps {
  open: boolean;
  business?: {
    aboutTheBrand?: string | null;
    logoUrl?: string | null;
    socialMediasConnected?: Array<{
      socialMediaName?: string | null;
    }> | null;
  } | null;
  onRequestClose: () => void;
}

const iconByRecommendationId: Record<
  CampaignPostCreateRecommendation["id"],
  React.ReactNode
> = {
  social: <InstagramOutlined />,
  description: <FileTextOutlined />,
  logo: <PictureOutlined />,
};

const CampaignPostCreateSuccessModal: React.FC<
  CampaignPostCreateSuccessModalProps
> = ({ open, business, onRequestClose }) => {
  const navigate = useNavigate();
  const isDesktopOrTablet = !isMobile || isTablet;

  const hasConnectedSocialMedia = useMemo(() => {
    return (
      business?.socialMediasConnected?.some((socialMedia) => {
        const name = socialMedia?.socialMediaName?.toLowerCase?.();
        return ["instagram", "meta", "tiktok"].includes(name || "");
      }) || false
    );
  }, [business?.socialMediasConnected]);

  const hasBusinessDescription = useMemo(() => {
    return (business?.aboutTheBrand || "").trim().length > 0;
  }, [business?.aboutTheBrand]);

  const hasBusinessLogo = useMemo(() => {
    return (business?.logoUrl || "").trim().length > 0;
  }, [business?.logoUrl]);

  const recommendations = useMemo<CampaignPostCreateRecommendation[]>(() => {
    const nextRecommendations: CampaignPostCreateRecommendation[] = [];

    if (!hasConnectedSocialMedia) {
      nextRecommendations.push({
        id: "social",
        title: "Connect social media",
        description: "Help creators understand your brand and content style.",
        ctaLabel: "Connect",
        onClick: () => navigate("/settings?tab=business-info"),
      });
    }

    if (!hasBusinessDescription) {
      nextRecommendations.push({
        id: "description",
        title: "Complete business description",
        description:
          "Tell creators what makes your business unique and why they should visit.",
        ctaLabel: "Edit",
        onClick: () => navigate("/settings?tab=business-info"),
      });
    }

    if (!hasBusinessLogo) {
      nextRecommendations.push({
        id: "logo",
        title: "Upload logo",
        description:
          "Build trust and help creators quickly recognize your brand.",
        ctaLabel: "Upload",
        onClick: () => navigate("/settings?tab=business-info"),
      });
    }

    return nextRecommendations.slice(0, 3);
  }, [
    hasBusinessDescription,
    hasBusinessLogo,
    hasConnectedSocialMedia,
    navigate,
  ]);

  const showRecommendations =
    isDesktopOrTablet && recommendations.length > 0;
  const showSkipAction = showRecommendations;

  if (!open) return null;

  return (
    <div
      className="campaign-success-modal__backdrop"
      onClick={onRequestClose}
      role="button"
      tabIndex={0}
      aria-label="Close campaign success modal"
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onRequestClose();
        }
      }}
      data-testid="campaign-success-modal-backdrop"
    >
      <div
        className="campaign-success-modal__container"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="campaign-success-modal__header">
          <h2>Campaign Created</h2>
          <button
            type="button"
            className="campaign-success-modal__icon-button"
            onClick={onRequestClose}
            aria-label="Close"
          >
            <CloseOutlined />
          </button>
        </div>

        <div className="campaign-success-modal__content">
          <div className="campaign-success-modal__banner">
            <div className="campaign-success-modal__check-icon">
              <CheckCircleFilled />
            </div>
            <h3>Campaign created successfully!</h3>
          </div>

          <div className="campaign-success-modal__schedule">
            <h4>You're all set — now we bring creators to you</h4>
            <p>
              We invite creators every <strong>Monday &amp; Thursday</strong>,
              and you'll be notified when applications come in.
            </p>
            <p>
              Most campaigns receive their first applications within{" "}
              <strong>2 business days</strong>.
            </p>
          </div>

          {showRecommendations && (
            <div className="campaign-success-modal__recommendations">
              <h4>Increase your chances of getting applications</h4>
              <div className="campaign-success-modal__recommendation-list">
                {recommendations.map((recommendation) => (
                  <div
                    key={recommendation.id}
                    className="campaign-success-modal__recommendation-item"
                  >
                    <div className="campaign-success-modal__recommendation-text">
                      <span className="campaign-success-modal__recommendation-icon">
                        {iconByRecommendationId[recommendation.id]}
                      </span>
                      <div>
                        <h5>{recommendation.title}</h5>
                        <p>{recommendation.description}</p>
                      </div>
                    </div>
                    <Button
                      type="primary"
                      onClick={recommendation.onClick}
                      className="campaign-success-modal__recommendation-cta"
                    >
                      {recommendation.ctaLabel}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="campaign-success-modal__footer">
          {showSkipAction && (
            <Button type="text" onClick={onRequestClose}>
              Skip for now
            </Button>
          )}
          <Button type="primary" onClick={onRequestClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CampaignPostCreateSuccessModal;
